import { useState } from "react";
import type { ArvorePiloto } from "@/types/piloto";

interface PilotTreeListProps {
  arvores: ArvorePiloto[];
  onSelectTree: (tree: ArvorePiloto) => void;
}

function getRiscoLabel(risco: string): string {
  switch (risco) {
    case "baixo": return "Baixo";
    case "medio": return "Médio";
    case "alto": return "Alto";
    case "critico": return "Crítico";
    default: return risco;
  }
}

function getRiscoBadgeColor(risco: string): string {
  switch (risco) {
    case "baixo": return "bg-primary-100 text-primary-700";
    case "medio": return "bg-caution-100 text-caution-700";
    case "alto": return "bg-warning-100 text-warning-700";
    case "critico": return "bg-danger-100 text-danger-700";
    default: return "bg-background-100 text-foreground-500";
  }
}

function getEstadoLabel(e: string): string {
  switch (e) {
    case "saudavel": return "Saudável";
    case "regular": return "Regular";
    case "comprometida": return "Comprometida";
    case "doente": return "Doente";
    case "morta": return "Morta";
    default: return e;
  }
}

function getOrigemLabel(o: string): string {
  switch (o) {
    case "nativa": return "Nativa";
    case "exotica": return "Exótica";
    case "nao_identificada": return "N/I";
    default: return o;
  }
}

export default function PilotTreeList({ arvores, onSelectTree }: PilotTreeListProps) {
  const [filterQuadra, setFilterQuadra] = useState("");
  const [filterEstado, setFilterEstado] = useState("");
  const [filterRisco, setFilterRisco] = useState("");
  const [filterEspecie, setFilterEspecie] = useState("");
  const [filterOrigem, setFilterOrigem] = useState("");

  const especiesUnicas = Array.from(new Set(arvores.map((a) => a.especiePopular).filter(Boolean)));
  const quadrasUnicas = Array.from(new Set(arvores.map((a) => a.quadraCodigo)));

  const filtered = arvores.filter((a) => {
    if (filterQuadra && a.quadraCodigo !== filterQuadra) return false;
    if (filterEstado && a.estadoFitossanitario !== filterEstado) return false;
    if (filterRisco && a.risco !== filterRisco) return false;
    if (filterEspecie && a.especiePopular !== filterEspecie) return false;
    if (filterOrigem && a.origem !== filterOrigem) return false;
    return true;
  });

  return (
    <div className="bg-white rounded-card border border-background-200/70 overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-background-200/70">
        <div className="flex items-center gap-2 mb-2">
          <i className="ri-plant-line text-primary-500 text-sm"></i>
          <h3 className="text-sm font-semibold text-foreground-800 font-heading">Árvores Cadastradas</h3>
          <span className="text-[10px] font-medium text-foreground-500 bg-background-100 px-2 py-0.5 rounded-full">
            {filtered.length} de {arvores.length}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={filterQuadra} onChange={(e) => setFilterQuadra(e.target.value)} className="text-xs border border-background-200 rounded-md px-2 py-1 bg-white text-foreground-700 focus:outline-none focus:border-accent-400">
            <option value="">Todas as quadras</option>
            {quadrasUnicas.map((q) => (<option key={q} value={q}>{q}</option>))}
          </select>
          <select value={filterEstado} onChange={(e) => setFilterEstado(e.target.value)} className="text-xs border border-background-200 rounded-md px-2 py-1 bg-white text-foreground-700 focus:outline-none focus:border-accent-400">
            <option value="">Todos estados</option>
            <option value="saudavel">Saudável</option>
            <option value="regular">Regular</option>
            <option value="comprometida">Comprometida</option>
            <option value="doente">Doente</option>
            <option value="morta">Morta</option>
          </select>
          <select value={filterRisco} onChange={(e) => setFilterRisco(e.target.value)} className="text-xs border border-background-200 rounded-md px-2 py-1 bg-white text-foreground-700 focus:outline-none focus:border-accent-400">
            <option value="">Todos riscos</option>
            <option value="baixo">Baixo</option>
            <option value="medio">Médio</option>
            <option value="alto">Alto</option>
            <option value="critico">Crítico</option>
          </select>
          <select value={filterEspecie} onChange={(e) => setFilterEspecie(e.target.value)} className="text-xs border border-background-200 rounded-md px-2 py-1 bg-white text-foreground-700 focus:outline-none focus:border-accent-400">
            <option value="">Todas espécies</option>
            {especiesUnicas.map((e) => (<option key={e} value={e}>{e}</option>))}
          </select>
          <select value={filterOrigem} onChange={(e) => setFilterOrigem(e.target.value)} className="text-xs border border-background-200 rounded-md px-2 py-1 bg-white text-foreground-700 focus:outline-none focus:border-accent-400">
            <option value="">Todas origens</option>
            <option value="nativa">Nativa</option>
            <option value="exotica">Exótica</option>
            <option value="nao_identificada">N/I</option>
          </select>
          <button
            onClick={() => { setFilterQuadra(""); setFilterEstado(""); setFilterRisco(""); setFilterEspecie(""); setFilterOrigem(""); }}
            className="text-xs text-foreground-500 hover:text-primary-600 transition-colors"
          >
            Limpar filtros
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-background-50 border-b border-background-200/70">
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">Código</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">Espécie</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">Quadra</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">Origem</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">DAP</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">Altura</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">Estado</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">Risco</th>
              <th className="px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider">Manejo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-200/50">
            {filtered.map((a) => (
              <tr
                key={a.id}
                className="hover:bg-background-50/50 transition-colors cursor-pointer"
                onClick={() => onSelectTree(a)}
              >
                <td className="px-3 py-2">
                  <span className="text-xs font-mono font-medium text-foreground-800">{a.codigo}</span>
                </td>
                <td className="px-3 py-2">
                  <div>
                    <p className="text-xs font-medium text-foreground-800">{a.especiePopular || "N/I"}</p>
                    {a.especieCientifica && (
                      <p className="text-[9px] text-foreground-500 italic">{a.especieCientifica}</p>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <span className="text-xs text-foreground-600">{a.quadraCodigo}</span>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                    a.origem === "nativa" ? "bg-primary-100 text-primary-700" : a.origem === "exotica" ? "bg-accent-100 text-accent-700" : "bg-background-100 text-foreground-500"
                  }`}>
                    {getOrigemLabel(a.origem)}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className="text-xs text-foreground-700">{a.dapCm ? `${a.dapCm} cm` : "—"}</span>
                </td>
                <td className="px-3 py-2">
                  <span className="text-xs text-foreground-700">{a.alturaM ? `${a.alturaM} m` : "—"}</span>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] font-medium ${
                    a.estadoFitossanitario === "saudavel" ? "text-primary-600" :
                    a.estadoFitossanitario === "regular" ? "text-caution-600" :
                    a.estadoFitossanitario === "comprometida" ? "text-warning-600" :
                    a.estadoFitossanitario === "doente" ? "text-orange-600" : "text-danger-600"
                  }`}>
                    {getEstadoLabel(a.estadoFitossanitario)}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${getRiscoBadgeColor(a.risco)}`}>
                    {getRiscoLabel(a.risco)}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className="text-[10px] text-foreground-600">
                    {a.manejoRecomendado.slice(0, 2).join(", ")}
                    {a.manejoRecomendado.length > 2 && ` +${a.manejoRecomendado.length - 2}`}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <div className="p-6 text-center">
          <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-2">
            <i className="ri-tree-line text-foreground-400 text-lg"></i>
          </div>
          <p className="text-xs text-foreground-500">Nenhuma árvore encontrada com os filtros atuais</p>
        </div>
      )}
    </div>
  );
}